import { useCallback, useMemo, useState } from 'react';
import type { SavedCreativeFilters } from '../types/hypothesis';
import { DEFAULT_SAVED_CREATIVE_FILTERS } from '../types/hypothesis';
import type { SavedCreativeFilterSummaryRow } from '../utils/savedCreativeFilters';
import {
  areSavedCreativeFiltersEqual,
  clearSavedCreativeFiltersEntry,
  formatSavedCreativeFiltersSummary,
  formatSavedFilterAtLabel,
  getSavedCreativeFiltersEntry,
  normalizeSavedCreativeFilters,
  setSavedCreativeFiltersEnabled,
  setSavedCreativeFiltersEntry,
  subtractSavedCreativeFilters,
} from '../utils/savedCreativeFilters';

interface UseSavedCreativeFiltersArgs {
  app: string;
  currentFilters: SavedCreativeFilters;
}

interface UseSavedCreativeFiltersResult {
  /** Whether a saved filter record exists (regardless of switch state). */
  hasSaved: boolean;
  /** Whether the saved filter is currently applied (switch on). */
  enabled: boolean;
  isDirty: boolean;
  savedAt: string | null;
  savedAtLabel: string;
  savedFilters: SavedCreativeFilters | null;
  savedSummaryLines: SavedCreativeFilterSummaryRow[];
  /**
   * Turn the switch on.
   * - First time: saves current filters; returns null (already applied).
   * - Re-enable: keeps saved snapshot; returns filters the caller should apply.
   */
  enableSaved: () => SavedCreativeFilters | null;
  /**
   * Turn the switch off — keeps the saved snapshot, does not mutate it.
   * Returns current filters with saved values removed so extras stay applied.
   */
  disableSaved: () => SavedCreativeFilters | null;
  updateSaved: () => void;
  clearSaved: () => void;
}

const useSavedCreativeFilters = ({
  app,
  currentFilters,
}: UseSavedCreativeFiltersArgs): UseSavedCreativeFiltersResult => {
  const [storageTick, setStorageTick] = useState(0);

  const entry = useMemo(() => {
    void storageTick;
    if (!app) return null;
    return getSavedCreativeFiltersEntry(app);
  }, [app, storageTick]);

  const hasSaved = entry !== null;
  const enabled = Boolean(entry?.enabled);
  const savedAt = entry?.savedAt ? entry.savedAt : null;

  const savedFilters = useMemo(
    () => (entry ? normalizeSavedCreativeFilters(entry.filters) : null),
    [entry],
  );

  const normalizedCurrent = useMemo(
    () => normalizeSavedCreativeFilters(currentFilters),
    [currentFilters],
  );

  const isDirty = useMemo(() => {
    if (!enabled || !savedFilters) return false;
    return !areSavedCreativeFiltersEqual(normalizedCurrent, savedFilters);
  }, [enabled, normalizedCurrent, savedFilters]);

  const savedSummaryLines = useMemo(
    () =>
      savedFilters
        ? formatSavedCreativeFiltersSummary(savedFilters)
        : formatSavedCreativeFiltersSummary(DEFAULT_SAVED_CREATIVE_FILTERS),
    [savedFilters],
  );

  const savedAtLabel = formatSavedFilterAtLabel();

  const bumpStorage = useCallback(() => {
    setStorageTick((tick) => tick + 1);
  }, []);

  const enableSaved = useCallback((): SavedCreativeFilters | null => {
    if (!app) return null;

    const existing = getSavedCreativeFiltersEntry(app);
    if (existing) {
      setSavedCreativeFiltersEnabled(app, true);
      bumpStorage();
      return normalizeSavedCreativeFilters(existing.filters);
    }

    setSavedCreativeFiltersEntry(app, {
      enabled: true,
      filters: normalizedCurrent,
      savedAt: new Date().toISOString(),
    });
    bumpStorage();
    return null;
  }, [app, bumpStorage, normalizedCurrent]);

  const disableSaved = useCallback((): SavedCreativeFilters | null => {
    if (!app) return null;

    const existing = getSavedCreativeFiltersEntry(app);
    if (!existing) return null;

    const remaining = subtractSavedCreativeFilters(
      normalizedCurrent,
      existing.filters,
    );
    setSavedCreativeFiltersEnabled(app, false);
    bumpStorage();
    return remaining;
  }, [app, bumpStorage, normalizedCurrent]);

  const updateSaved = useCallback(() => {
    if (!app || !enabled) return;
    setSavedCreativeFiltersEntry(app, {
      enabled: true,
      filters: normalizedCurrent,
      savedAt: new Date().toISOString(),
    });
    bumpStorage();
  }, [app, bumpStorage, enabled, normalizedCurrent]);

  const clearSaved = useCallback(() => {
    if (!app) return;
    clearSavedCreativeFiltersEntry(app);
    bumpStorage();
  }, [app, bumpStorage]);

  return {
    hasSaved,
    enabled,
    isDirty,
    savedAt,
    savedAtLabel,
    savedFilters,
    savedSummaryLines,
    enableSaved,
    disableSaved,
    updateSaved,
    clearSaved,
  };
};

export default useSavedCreativeFilters;
